import BaseService from '../base-service';
import NetworkUrls from '../network-urls';
import moment from 'moment';

class RoomService extends BaseService {
  fetch(id, lang = 'FR') {
    const config = {
      method: 'POST',
      url: NetworkUrls.graphql,
      data: {
        query: `query ($lang: Lang!, $roomId: CID!, $color: String, $start: Date!, $end: Date!) {
          Room {
              one (id: $roomId) {
                  id,
                  name,
                  is_one_day,
                  position {
                    lat,
                    lng
                  }
                  label (lang: $lang) {
                    long
                  }
                  capacity_max,
                  color,
                  area,
                  pictures (sortBy: NUMBER, limit: 100) {
                      absolute_path
                  },
                  meta (lang: $lang) {
                    title,
                    description
                  },
                  text (lang: $lang) {
                      name,
                      title,
                      description,
                      equipments,
                      short_name
                  },
                  address {
                    inline
                  },
                  room_group {
                      id,
                      text (lang: $lang) {
                          name
                      },
                      room_zone {
                          id,
                          name
                      }
                  },
                  equipments {
                    icon (color: $color)
                  },
                  provider,
                  lock_intervals (start: $start, end: $end) {
                    from,
                    to,
                    can_start_after,
                    can_end_before
                  }
              }
          }
        }`,
        variables: {
          lang,
          color: '#ffffff',
          roomId: id,
          start: moment().format('YYYY-MM-DD'),
          end: moment().add(365, 'd').format('YYYY-MM-DD')
        },
      },
    };
    return this._exec(config);
  }

  fetchPreBookableInfos(roomIds, dates, nbGuest, roomGroup = null, lang = 'FR', input = {}, sameDate = false) {
    const query = `query ($roomIds: [CID], $dates: [Date], $lang: Lang!, $nbGuest: Int!, $roomGroup: CID!, $hasGroup: Boolean!, $searchParams: SearchParamInput!, $sameDate:Boolean!) {
          RoomGroup @include(if: $hasGroup) {
            one (id: $roomGroup) {
              text (lang: $lang) {
                name
              }
            }
          },
          Room {
              many (in: $roomIds, roomGroup: $roomGroup, searchParams: $searchParams, limit: 100) {
                  id,
                  area,
                  is_one_day,
                  position {
                      lat,
                      lng
                  }
                  pictures (onlyMain: false, limit: 3, offset: 0) {
                    absolute_path
                  },
                  text (lang: $lang) {
                    title,
                    short_name
                  },
                  equipments {
                    icon
                  },
                  provider,
                  price_plans (dates: $dates, nbGuest: $nbGuest, sameDate: $sameDate) {
                      date,
                      input_prices {
                          nbGuest,
                          edges {
                              hostingPrice,
                              additionalPrice,
                              touristTax,
                              totalPrice,
                              from {
                                  id,
                                  label,
                                  display_order,
                                  text (lang: $lang) {
                                      color,
                                      left_label,
                                      title,
                                      sub_title,
                                      description
                                  }
                              }
                          }
                      }
                  }
              }
          }
        }`;
    const variables = {
      roomIds, dates, lang, nbGuest, sameDate, roomGroup: roomGroup || -1, hasGroup: !!roomGroup, searchParams: {
        businessCode: input.businessCode,
        hasInfant: !!input.hasInfant,
        searchString: input.searchString
      }
    };
    const config = {
      method: 'POST',
      url: NetworkUrls.graphql,
      data: {
        query,
        variables,
      },
    };
    return this._exec(config);
  }

  fetchPreBookableGroupsInfos(roomIds, dates, lang, nbGuest, input, sameDate) {
    const query = `query ($roomIds: [CID], $dates: [Date], $lang: Lang!, $nbGuest: Int!, $searchParams: SearchParamInput!, $sameDate: Boolean!) {
      RoomGroup {
          many (searchParams: $searchParams) {
              id,
              text (lang: $lang) {
                 name,
                 display_address
              },
              rooms (in: $roomIds, searchParams: $searchParams) {
                  id,
                  is_one_day,
                  position {
                      lat,
                      lng
                  },
                  provider
                  pictures (onlyMain: false, limit: 4, offset: 0) {
                    absolute_path
                  },
                  main_picture: pictures (onlyMain: true, limit: 1) {
                    absolute_path
                  }
                  price_plans (dates: $dates, sameDate: $sameDate, nbGuest: $nbGuest) {
                      date,
                      input_prices {
                          nbGuest,
                          edges {
                              totalPrice,
                              hostingPrice,
                              additionalPrice,
                                extraAdditionalPrice,
                                optionsBasePrice,
                                optionsAdditionalPrice,
                              touristTax
                          }
                      }
                  }
              }
          }
      }
    }`;
    const variables = {
      roomIds, dates, lang, nbGuest, sameDate,
      searchParams: {
        businessCode: input.businessCode,
        hasInfant: !!input.hasInfant,
        searchString: input.searchString
      }
    };
    const config = {
      method: 'POST',
      url: NetworkUrls.graphql,
      data: {
        query,
        variables,
      },
    };
    return this._exec(config);
  }

  fetchRoomAvailability({fromDate, toDate, traveler, equipments}) {
    const config = {
      method: 'POST',
      url: NetworkUrls.roomAvailability,
      data: {
        fromDate: moment(fromDate).format('YYYY-MM-DD'),
        toDate: moment(toDate).format('YYYY-MM-DD'),
        traveler,
        equipments
      },
    };
    return this._exec(config, false);
  }

  async fetchEquipments({lang}) {
    const query = `query ($lang: Lang!, $limit: Int!, $offset: Int!) {
        RoomEquipment {
            many (limit: $limit, offset: $offset) {
                id,
                not_stackable_with,
                text (lang: $lang) {
                    name
                }
            }
        }
    }`;
    const limit = 100;
    const offset = 0;
    const variables = {lang, limit, offset};
    const config = {
      method: 'POST',
      url: NetworkUrls.graphql,
      data: {
        query,
        variables
      }
    };
    return this._exec(config);
  }

  async addFavorite(room_id, accessToken) {
    const query = `mutation ($id: CID!) {
        Room {
            addFavorite (room_id: $id) {
                inserted
            }
        }
    }`;
    const variables = {id: room_id};
    const config = {
      method: 'POST',
      url: NetworkUrls.graphql,
      data: {
        query,
        variables
      },
      headers: {
        Authorization: accessToken
      }
    };
    return this._exec(config);
  }

  async removeFavorite(room_id, accessToken) {
    const query = `mutation ($id: CID!) {
        Room {
            removeFavorite (room_id: $id) {
                deleted
            }
        }
    }`;
    const variables = {id: room_id};
    const config = {
      method: 'POST',
      url: NetworkUrls.graphql,
      headers: {
        Authorization: accessToken
      },
      data: {
        query,
        variables
      }
    };
    return this._exec(config);
  }

  async queryPrices(args, accessToken = null) {
    const query = `
        fragment prices on RoomPriceEdge {
            hostingPrice,
            additionalPrice,
            extraAdditionalPrice,
            optionsBasePrice,
            optionsAdditionalPrice,
            touristTax,
            totalPrice,
            from {
                id,
                label,
                display_order,
                text (lang: $lang) {
                  color,
                  left_label,
                  title,
                  sub_title,
                  description
                }
            }
        }
        query ($dates: [Date], $travelers: TravelersInput!, $id: CID!, $lang: Lang!) {
        Room {
            one (id: $id) {
              provider,
              price_plans (dates: $dates, travelers: $travelers) {
                  date,
                  input_prices {
                      nbGuest,
                      edges {
                          ...prices
                          altPrice {
                            ...prices
                          }
                      }
                  }
              }
            }
        }
    }`;
    const variables = args;
    const config = {
      method: 'POST',
      url: NetworkUrls.graphql,
      headers: {
        Authorization: accessToken
      },
      data: {
        query,
        variables
      }
    };
    return this._exec(config);
  }
}

export const roomService = new RoomService();



// WEBPACK FOOTER //
// ./src/network/services/room.service.js